import React, {useContext} from "react";
import "./alertTile.scss";
import {AlertModel} from "../../../models/alert";
import Button from "../../../../common/components/shared/button/button";
import {Chip} from "@mui/material";
import i18n from "../../../../i18n/i18n";
import {Utils} from "../../../../helpers/utils";
import {AlertsService} from "../../../services/alertsService";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {Api} from "../../../../helpers/api";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {DateUtils} from "../../../../helpers/dateUtils";
import {getNotificationMessage} from "../../../../helpers/notificationMessages";
import {storesContext} from "../../../../common/stores/stores";

interface AlertTileProps {
    data: AlertModel;
    reload: () => Promise<void>;
}

export default function AlertTile(props: AlertTileProps) {
    const userStore = useContext(storesContext).userStore;
    const isRead = props.data.isRead;
    const isResolved = props.data.isResolved;
    const highlight = isRead || isResolved ? "" : "highlight";
    const [responseRead, setResponseRead] = React.useState<AxiosResponse<HttpErrorModel[] | any> | undefined>(undefined);
    const [responseResolve, setResponseResolve] = React.useState<AxiosResponse<HttpErrorModel[] | any> | undefined>(undefined);
    const [isWaitingRead, setIsWaitingRead] = React.useState<boolean>(false);
    const [isWaitingResolve, setisWaitingResolve] = React.useState<boolean>(false);

    return (
        <div className={`alert-tile ${props.data.type} ${highlight}`} data-testid={`alert-${props.data.id}`}>
            {renderReadAndResolve()}
            {renderDescription()}
            {renderAllPropertyChips()}
        </div>
    );


    function renderReadAndResolve() {
        return (
            <div className="read-resolve">
                {renderReadAndResolvedChips()}
                {renderReadAndResolveButtons()}
            </div>);
    }

    function renderReadAndResolvedChips() {
        const isRead = props.data.isRead;
        const isResolved = props.data.isResolved;
        const not = i18n.t("alerts.status.not");
        const readMessageSuffix = isRead ? "" : not;
        const resolvedMessageSuffix = isResolved ? "" : not;
        const readMessage = `${readMessageSuffix}${i18n.t("alerts.status.isRead")}`;
        const resolvedMessage = `${resolvedMessageSuffix}${i18n.t("alerts.status.isResolved")}`;
        const readClass = isRead ? "isRead highlight-status" : "isRead";
        const resolvedClass = isResolved ? "isResolved highlight-status" : "isResolved";

        return (
            <div className="isRead-isResolved-chips">
                <Chip
                    label={Utils.upperCaseFirstLetter(readMessage)}
                    className={readClass}
                    data-testid="isRead-chip"
                />
                <Chip
                    label={Utils.upperCaseFirstLetter(resolvedMessage)}
                    className={resolvedClass}
                    data-testid="isResolved-chip"
                />
            </div>
        );
    }

    function renderReadAndResolveButtons() {
        const isRead = props.data.isRead;
        const isResolved = props.data.isResolved;
        const readMessage = isRead ? i18n.t("alerts.status.isRead") : i18n.t("alerts.actions.read");
        const resolveMessage = isResolved ? i18n.t("alerts.status.isResolved") : i18n.t("alerts.actions.resolve");
        const readClass = isRead ? "read" : "read active";
        const resolveClass = isResolved ? "resolve" : "resolve active";

        return (
            <div className="read-resolve-buttons">
                <Button
                    text={Utils.upperCaseFirstLetter(readMessage)}
                    type="secondary"
                    className={readClass}
                    disabled={isRead}
                    variant="outlined"
                    testId="read-button"
                    onClick={() => readAlert()}
                    isWaiting={isWaitingRead}
                />
                <Button
                    text={Utils.upperCaseFirstLetter(resolveMessage)}
                    type="secondary"
                    className={resolveClass}
                    disabled={isResolved}
                    variant="outlined"
                    testId="resolve-button"
                    onClick={() => resolveAlert()}
                    isWaiting={isWaitingResolve}
                />
                {!Api.isSuccess(responseRead) && (
                    <HttpError
                        response={responseRead}
                        cause={i18n.t("alerts.readErrorSuffix", {id: props.data.id})}
                    />
                )}
                {!Api.isSuccess(responseResolve) && (
                    <HttpError
                        response={responseResolve}
                        cause={i18n.t("alerts.resolveErrorSuffix", {id: props.data.id})}
                    />
                )}
            </div>
        );
    }

    function renderDescription() {
        return <div className="description">
            {getNotificationMessage(props.data.description.code, props.data.description.attributes)}
        </div>;
    }

    function renderAllPropertyChips() {
        const chips: JSX.Element[] = [];

        addPropertyChip(chips, i18n.t("alerts.property.date"), DateUtils.getFormattedTimeWithDate(props.data.tags.date).replace("-", " "));
        addPropertyChip(chips, i18n.t("alerts.property.location"), props.data.tags.locationName || undefined);
        addPropertyChip(chips, (i18n.t("alerts.property.bookingNo")), props.data.tags.bookingNo || undefined);
        addPropertyChip(chips, i18n.t("alerts.property.trailerRegNo"), props.data.tags.trailerRegNo || undefined);
        addPropertyChip(chips, i18n.t("alerts.property.lorryRegNo"), props.data.tags.lorryRegNo || undefined);
        addPropertyChip(chips, i18n.t("alerts.property.wagonNo"), props.data.tags.wagonNo || undefined);
        addPropertyChip(chips, i18n.t("alerts.property.containerNo"), props.data.tags.containerNo || undefined);

        return <div className="property-chips">
            {chips}
        </div>;
    }

    function addPropertyChip(chipsArray: JSX.Element[], label: string, data: string | undefined) {
        if (!data) {
            return null;
        }

        chipsArray.push(<Chip
            key={`alert-${props.data.id}-property-${chipsArray.length}`}
            label={<div>
                <span className="label">{label}:</span>
                <span className="value">{data}</span>
            </div>}
            className="property-chip"
            variant="outlined"
        />);
    }

    async function readAlert() {
        setIsWaitingRead(true);
        setResponseRead(undefined);
        const responseRead = await AlertsService.readAlert(props.data.id) || undefined;
        setResponseRead(responseRead);

        if (Api.isSuccess(responseRead)) {
            await props.reload();
            await getCountOfUnreadAlerts();
        }
        setIsWaitingRead(false);
    }

    async function resolveAlert() {
        setisWaitingResolve(true);
        setResponseResolve(undefined);
        const responseResolve = await AlertsService.resolveAlert(props.data.id) || undefined;
        setResponseResolve(responseResolve);

        if (Api.isSuccess(responseResolve)) {
            await props.reload();
            await getCountOfUnreadAlerts();
        }
        setisWaitingResolve(false);
    }

    async function getCountOfUnreadAlerts() {
        const responseCountOfAlerts = await AlertsService.getCountOfUnreadAlerts();
        userStore.setUnreadAlertsCount(responseCountOfAlerts?.data?.count);
    }
}
